import React, { useMemo, useState, useCallback } from 'react';
import { Modal, Button } from 'grab-ui';
import { Divider, Checkbox } from 'antd';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import DropOffBeforeRO from 'public/images/drop-off-before-route-optimize.svg';
import DropOffAfterRO from 'public/images/drop-off-after-route-optimize.svg';
import ThreeArrow from 'public/images/three-arrow.svg';
import formatCurrency from 'common/utils/format-currency';
import calcPrice from 'common/utils/calc-price';

import styles from './route-optimization-modal.less';

const RouteOptimizationModal = ({
  visible,
  numDeliveries,
  optimizedAmount,
  currentAmount,
  currency,
  onOk,
  onCancel,
}) => {
  const [checkedDoNotShowAgain, setCheckedDoNotShowAgain] = useState(false);

  const optimizedAmountDisplay = useMemo(
    () =>
      formatCurrency({
        code: _get(currency, 'code'),
        amount: calcPrice(optimizedAmount, currency),
      }),
    [optimizedAmount, currency],
  );

  const currentAmountDisplay = useMemo(
    () =>
      formatCurrency({
        code: _get(currency, 'code'),
        amount: calcPrice(currentAmount, currency),
      }),
    [currentAmount, currency],
  );

  const saveAmountDisplay = useMemo(
    () =>
      formatCurrency({
        code: _get(currency, 'code'),
        amount: calcPrice(currentAmount - optimizedAmount, currency),
      }),
    [optimizedAmount, currentAmount, currency],
  );

  const modalBodyImage = useMemo(
    () => (
      <div className={styles.explainText}>
        <div className={styles.currentTotal}>
          <img src={DropOffBeforeRO} alt="" className={styles.priceImg} />
          <div className={styles.title}>
            {I18n.t(
              'bulkUploadRevamp.step3.routeOptimizationModal.currentTotal',
            )}
          </div>
          <div className={styles.price}>{currentAmountDisplay}</div>
        </div>

        <img src={ThreeArrow} className={styles.threeArrow} alt="" />

        <div className={styles.newTotal}>
          <img src={DropOffAfterRO} alt="" className={styles.priceImg} />
          <div className={styles.title}>
            {I18n.t(
              'bulkUploadRevamp.step3.routeOptimizationModal.totalAfterMultiStopMode',
            )}
          </div>
          <div className={styles.price}>{optimizedAmountDisplay}</div>
        </div>
      </div>
    ),
    [currentAmountDisplay, optimizedAmountDisplay],
  );

  const onCheckDoNotShowAgain = useCallback(
    event => {
      setCheckedDoNotShowAgain(event.target.checked);
    },
    [setCheckedDoNotShowAgain],
  );

  const countNumDeliveries = useMemo(
    () => (
      <span className={styles.numDeliveries}>
        {I18n.t('bulkUploadRevamp.step3.routeOptimizationModal.deliveryCount', {
          numDeliveries,
        })}
      </span>
    ),
    [numDeliveries],
  );

  const modalBody = useMemo(
    () => (
      <div className={styles.body}>
        {modalBodyImage}
        <div className={styles.descriptionTextWrapper}>
          <span>
            {I18n.t('bulkUploadRevamp.step3.routeOptimizationModal.groupText1')}
          </span>
          {countNumDeliveries}
          <span>
            {' '}
            {I18n.t('bulkUploadRevamp.step3.routeOptimizationModal.groupText2')}
          </span>
        </div>
      </div>
    ),
    [modalBodyImage, countNumDeliveries],
  );

  const modalFooter = useMemo(
    () => (
      <div className={styles.footer}>
        <div className={styles.checkbox}>
          <Checkbox
            checked={checkedDoNotShowAgain}
            onChange={onCheckDoNotShowAgain}
          >
            {I18n.t(
              'bulkUploadRevamp.step3.routeOptimizationModal.dontShowAgain',
            )}
          </Checkbox>
        </div>
        <div className={styles.buttonWrapper}>
          <Button onClick={onCancel(checkedDoNotShowAgain)} size="large">
            {I18n.t('bulkUploadRevamp.step3.routeOptimizationModal.skip')}
          </Button>
          <Button
            type="primary"
            onClick={onOk(checkedDoNotShowAgain)}
            size="large"
          >
            {I18n.t(
              'bulkUploadRevamp.step3.routeOptimizationModal.tryMultiStopMode',
            )}
          </Button>
        </div>
      </div>
    ),
    [onCancel, onOk, checkedDoNotShowAgain, onCheckDoNotShowAgain],
  );

  return (
    <Modal
      visible={visible}
      className={styles.routeOptimizationModal}
      footer={null}
      closable={false}
    >
      <p className={styles.header}>
        {I18n.t('bulkUploadRevamp.step3.routeOptimizationModal.saveAmount', {
          amount: saveAmountDisplay,
        })}
      </p>
      {modalBody}
      <Divider />
      {modalFooter}
    </Modal>
  );
};

RouteOptimizationModal.defaultProps = {
  currency: null,
};

RouteOptimizationModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  numDeliveries: PropTypes.number.isRequired,
  optimizedAmount: PropTypes.number.isRequired,
  currency: PropTypes.shape({}),
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  currentAmount: PropTypes.number.isRequired,
};

export default RouteOptimizationModal;
